/* eslint-disable no-param-reassign */
import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import europeMap from 'src/assets/json/europeMap.json';
import { API_URLS } from '../../constants/ApiUrls';
import { eosGet } from '../../services/EosApi';
import {
  ICountryWithSupplyContracts,
  IDashboardEOSState,
  IGeoJSONFeatureCollection,
  IMapFeature,
  IPortfolio,
  ITopoJSONTopology,
} from '../types/dashboard.types';

export const initialState: IDashboardEOSState = {
  countrieswithContracts: [],
  uniqueCountries: [],
  uniqueContracts: [],
  loading: false,
  error: null,
  mapData: [],
  portfolio: {
    loading: false,
    portfolios: [],
    selectedPortfolioId: null,
    error: null,
  },
  scenarioFilters: {
    selectedCountryId: null,
    selectedContractId: null,
  },
};

export const getPortfolios = createAsyncThunk(
  'dashboard/getPortfolios',
  async (_, { rejectWithValue }) => {
    try {
      const response = await eosGet(API_URLS.EOS_PORTFOLIO);
      return response.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error occurred');
    }
  },
);

export const getCountriesContracts = createAsyncThunk(
  'dashboard/getCountriesContracts',
  async (
    {
      portfolioId,
      userId,
    }: {
      portfolioId: number;
      userId: number | null;
    },
    { rejectWithValue },
  ) => {
    try {
      const queryParams = new URLSearchParams({
        portfolioId: String(portfolioId),
      });

      if (userId !== null) {
        queryParams.set('userId', String(userId));
      }

      const response = await eosGet(
        `${API_URLS.EOS_MARKET_WITH_SUPPLY_CONTRACT}?${queryParams.toString()}`,
      );

      const { topojson } = await import('chartjs-chart-geo');

      const topology = europeMap as ITopoJSONTopology;
      const featureCollection = topojson.feature(
        topology as never,
        topology.objects.europe as never,
      ) as unknown as IGeoJSONFeatureCollection;

      const mapDataWithContracts: IMapFeature[] = featureCollection.features.map((feature) => {
        const countryName = feature.properties?.NAME as string;
        const countryData = response.data.find(
          (country: ICountryWithSupplyContracts) => country.country === countryName,
        );

        return {
          type: 'Feature',
          id: feature.id,
          geometry: feature.geometry,
          properties: {
            ...feature.properties,
            NAME: countryName,
            supplyContracts: countryData ? countryData.supplyContracts : [],
          },
        } as IMapFeature;
      });

      return {
        data: response.data,
        portfolioId,
        mapData: mapDataWithContracts,
      };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error occurred');
    }
  },
);

const dashboardSlice = createSlice({
  name: 'dashboardEOS',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetDashboard: (state) => {
      state.countrieswithContracts = [];
      state.uniqueCountries = [];
      state.uniqueContracts = [];
      state.loading = false;
      state.error = null;
      state.mapData = [];
    },
    setSelectedPortfolio: (state, action: PayloadAction<number>) => {
      state.portfolio.selectedPortfolioId = action.payload;
    },
    setSelectedCountry: (
      state,
      action: PayloadAction<{ countryId: number; countryName: string }>,
    ) => {
      state.scenarioFilters.selectedCountryId = action.payload.countryId;
      // Reset contract selection when country changes
      state.scenarioFilters.selectedContractId = null;
    },
    setSelectedContract: (
      state,
      action: PayloadAction<{
        contractId: number | null;
      }>,
    ) => {
      state.scenarioFilters.selectedContractId = action.payload.contractId;
    },
    setSelectedCountryContract: (
      state,
      action: PayloadAction<{
        countryId: number;
        contractId: number;
      }>,
    ) => {
      state.scenarioFilters.selectedContractId = action.payload.contractId;
      state.scenarioFilters.selectedCountryId = action.payload.countryId;
    },
    clearScenarioFilters: (state) => {
      state.scenarioFilters = {
        selectedCountryId: null,
        selectedContractId: null,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // Portfolio reducers
      .addCase(getPortfolios.pending, (state) => {
        state.portfolio.loading = true;
        state.error = null;
      })
      .addCase(getPortfolios.fulfilled, (state, action: PayloadAction<IPortfolio[]>) => {
        state.portfolio.loading = false;
        state.portfolio.portfolios = action.payload;
        state.error = null;
      })
      .addCase(getPortfolios.rejected, (state, action) => {
        state.portfolio.loading = false;
        state.error = action.error.message ?? 'Failed to fetch portfolios';
      })
      // Countries and contracts reducers
      .addCase(getCountriesContracts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getCountriesContracts.fulfilled,
        (
          state,
          action: PayloadAction<{
            data: ICountryWithSupplyContracts[];
            portfolioId: number;
            mapData: IMapFeature[];
          }>,
        ) => {
          state.loading = false;
          state.countrieswithContracts = action.payload.data;
          state.portfolio.selectedPortfolioId = action.payload.portfolioId;

          state.uniqueCountries = Array.from(
            new Set(
              action.payload.data.map((item) => ({
                id: item.id,
                name: item.country,
              })),
            ),
          );

          const allContracts = action.payload.data.flatMap((item) => item.supplyContracts);
          const uniqueContractsMap = new Map();

          for (const contract of allContracts) {
            if (!uniqueContractsMap.has(contract.id)) {
              uniqueContractsMap.set(contract.id, contract);
            }
          }

          state.uniqueContracts = Array.from(uniqueContractsMap.values());
          state.error = null;
          state.mapData = action.payload.mapData;
        },
      )
      .addCase(getCountriesContracts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Failed to fetch countries and contracts';
      });
  },
});

export const {
  clearError,
  resetDashboard,
  setSelectedPortfolio,
  setSelectedCountry,
  setSelectedContract,
  setSelectedCountryContract,
  clearScenarioFilters,
} = dashboardSlice.actions;

type DashboardStateShape = { dashboardEOS: IDashboardEOSState };

export const selectMapData = (state: DashboardStateShape) => state.dashboardEOS.mapData;

export const getSelectedPortfolioId = (state: DashboardStateShape) =>
  state.dashboardEOS.portfolio.selectedPortfolioId;

export const getSelectedPortfolioName = (state: DashboardStateShape) => {
  const selectedId = state.dashboardEOS.portfolio.selectedPortfolioId;
  const portfolio = state.dashboardEOS.portfolio.portfolios.find((p) => p.id === selectedId);
  return portfolio ? portfolio.name : null;
};

export const getSelectedMarketName = (state: DashboardStateShape) => {
  const { selectedCountryId } = state.dashboardEOS.scenarioFilters;
  const country = state.dashboardEOS.uniqueCountries.find((c) => c.id === selectedCountryId);
  return country ? country.name : null;
};

export const getSelectedMarketId = (state: DashboardStateShape) =>
  state.dashboardEOS.scenarioFilters.selectedCountryId;

export const getSelectedContractId = (state: DashboardStateShape) =>
  state.dashboardEOS.scenarioFilters.selectedContractId;

export const getSelectedSupplyContractName = (state: DashboardStateShape) => {
  const { selectedContractId } = state.dashboardEOS.scenarioFilters;
  const contract = state.dashboardEOS.uniqueContracts.find((c) => c.id === selectedContractId);
  return contract ? contract.contractName : null;
};

export const getDashboardState = (state: DashboardStateShape) => state.dashboardEOS;

export default dashboardSlice.reducer;
